import {ApolloQueryResult, gql} from '@apollo/client';
import React, {useEffect, useRef} from 'react';

import {getApolloClient} from 'graphql-client/ApolloClient';
import {writeFragmentToCache} from 'graphql-client/ApolloWrappers';
import {
  QueryResultType,
  SkippableBenchlingQueryResult,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {
  graphQLArrayToREST,
  graphQLToREST,
  restToGraphQL,
} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {AssayResult, AssayRun} from 'results/assays/AssayRecordConstants';
import {
  NoteLinkedAssayDataRunQuery,
  NoteLinkedAssayDataRunQueryVariables,
} from 'runs/__generated/NoteLinkedAssayDataRunQuery';
import {NoteLinkedAssayData as NoteLinkedAssayDataData} from 'runs/AttachmentConstants';

export const RUN_QUERY = gql`
  query NoteLinkedAssayDataRunQuery($id: StrictString!, $offset: StrictInt!) @skipAudit {
    run(id: $id) {
      ... on RunNode {
        ...RunGraphQL
        linkedResults(limitOffsetInput: {limit: 100, offset: $offset}) {
          ... on ResultListingNode {
            total
            results {
              ...ResultGraphQL
            }
            resultCountBySchemaId
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('Run').fragment}
  ${getFragmentWrapper('Result').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * This function allows us to refetch the AssayRun if we have mutated it by doing an upload or changed the
 * tags in some way.
 *
 * Note that this doesn't clear out the linkedResults that belong to the Run in the cache.  We instead
 * just run the query with an offset of 0 which will grab the first page of linkedResults and the total number
 * of linkedResults that exist.  If the total number of linkedResults is larger than the page size and larger
 * than the number of linkedResults in the cache, then we'll fetch the appropriate page to grab the missing
 * linkedResults.  This is safe to do as Results are immutable once created.
 *
 * @param runId - the AssayRun warehouseId that we want to refetch
 */
export async function refetchRun(
  runId: string,
  run?: AssayRun
): Promise<ApolloQueryResult<NoteLinkedAssayDataRunQuery>> {
  if (run) {
    writeFragmentToCache(getFragmentWrapper('Run'), restToGraphQL('Run', run));
  }
  return await getApolloClient().query({
    query: RUN_QUERY,
    variables: {
      id: runId,
      offset: 0,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
}

interface RunComponentProps {
  assayRunInfo: {
    assayRunId: string | null;
  };
  updateHandler: (index: number, content: Partial<NoteLinkedAssayDataData>) => void;
  assayRun: AssayRun | null;
  assayResults: Array<AssayResult> | null;
  resultSchemaToCount: {[resultSchema: string]: number};
}

interface RunComponentEnhancedProps {
  assayRun: AssayRun | null;
  assayResults: Array<AssayResult> | null;
  resultSchemaToCount: {[resultSchema: string]: number};
  isLoading?: boolean;
}

function withRunFetching<P extends RunComponentProps, T = unknown>(
  WrappedComponent: React.ComponentType<P & RunComponentEnhancedProps>
): React.ComponentType<P> {
  return (props: P) => {
    const result = useSkippableBenchlingQuery<
      NoteLinkedAssayDataRunQuery,
      NoteLinkedAssayDataRunQueryVariables
    >(RUN_QUERY, {
      variables: {id: props.assayRunInfo.assayRunId!, offset: 0},
      fetchPolicy: 'cache-and-network',
      skip: props.assayRunInfo.assayRunId == null,
    });
    const ref =
      useRef<
        SkippableBenchlingQueryResult<NoteLinkedAssayDataRunQuery, NoteLinkedAssayDataRunQueryVariables>
      >();
    useEffect(() => {
      if (result.type === QueryResultType.SUCCESS && result.data && result.fetchMore) {
        ref.current = result;
        if (
          !isInaccessible(result.data.run) &&
          result.data.run.linkedResults.results.length < result.data.run.linkedResults.total
        ) {
          result.fetchMore({variables: {offset: result.data.run.linkedResults.results.length}});
        }
      }
    }, [result]);
    const prevResult = ref.current;
    if (result.type === QueryResultType.ERROR) {
      return null;
    } else if (prevResult === undefined && result.type === QueryResultType.PENDING) {
      return (
        <WrappedComponent
          {...props}
          assayRun={null}
          assayResults={null}
          resultSchemaToCount={{}}
          isLoading={true}
        />
      );
    } else if (result.type === QueryResultType.SKIPPED) {
      return <WrappedComponent {...props} assayRun={null} assayResults={null} resultSchemaToCount={{}} />;
    } else {
      let data;
      if (
        result.type === QueryResultType.PENDING &&
        prevResult &&
        prevResult.type === QueryResultType.SUCCESS
      ) {
        data = prevResult.data;
      } else if (result.type === QueryResultType.SUCCESS) {
        data = result.data;
      } else {
        return <WrappedComponent {...props} assayRun={null} assayResults={null} resultSchemaToCount={{}} />;
      }
      const assayRunGql = isInaccessible(data.run) ? null : data.run;
      const assayResultsGql = assayRunGql != null ? assayRunGql.linkedResults.results : null;
      const assayRun = assayRunGql != null ? graphQLToREST('Run', assayRunGql) : null;
      const assayResults = assayResultsGql != null ? graphQLArrayToREST('Result', assayResultsGql) : null;
      const resultSchemaToCount = assayRunGql != null ? assayRunGql.linkedResults.resultCountBySchemaId : {};
      const updateHandler = (index: number, content: Partial<NoteLinkedAssayDataData>): void => {
        const updatedTags = content.versionedData?.tags;
        if (assayRunGql != null && updatedTags) {
          const runWithUpdatedTags = {
            ...assayRun,
            id: assayRunGql.id,
            tags: updatedTags,
          } as AssayRun;
          result.refetch({id: assayRun?.id, offset: 0});
          writeFragmentToCache(getFragmentWrapper('Run'), restToGraphQL('Run', runWithUpdatedTags));
        }
        props.updateHandler(index, content);
      };
      return (
        <WrappedComponent
          {...props}
          assayRun={assayRun}
          assayResults={assayResults}
          updateHandler={updateHandler}
          resultSchemaToCount={resultSchemaToCount}
        />
      );
    }
  };
}

export default function withMaybeRunFetching<P extends RunComponentProps, T = unknown>(
  WrappedComponent: React.ComponentType<P & RunComponentEnhancedProps>
): React.ComponentType<P> {
  const WrappedWithRunPresent = withRunFetching(WrappedComponent);
  WrappedWithRunPresent.displayName = `withMaybeRunFetching(${WrappedComponent.displayName})`;
  return (props: P) => {
    if (props.assayRunInfo.assayRunId == null) {
      return <WrappedComponent {...props} />;
    } else {
      return <WrappedWithRunPresent {...props} assayRunInfo={{assayRunId: props.assayRunInfo.assayRunId}} />;
    }
  };
}
